import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Build as BuildIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface ToolCallCardProps {
  toolName: string;
  args: string;
  result?: string;
  isSuccess?: boolean;
  isRunning?: boolean;
}

export const ToolCallCard = ({ toolName, args, result, isSuccess, isRunning }: ToolCallCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const statusIcon = isRunning
    ? <BuildIcon sx={{ fontSize: 16, color: 'info.main', animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
    : isSuccess === true
      ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
      : isSuccess === false
        ? <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
        : null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        my: 0.5,
        bgcolor: 'action.hover',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.selected' },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        {statusIcon}
        <Chip label={toolName} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 12 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Arguments</Typography>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              bgcolor: 'background.default',
              p: 1,
              borderRadius: 0.5,
              overflow: 'auto',
              maxHeight: 200,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
              mt: 0.5,
            }}
          >
            {formatJson(args)}
          </Box>
          {result !== undefined && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 1, display: 'block' }}>
                Result
              </Typography>
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  bgcolor: 'background.default',
                  p: 1,
                  borderRadius: 0.5,
                  overflow: 'auto',
                  maxHeight: 200,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  m: 0,
                  mt: 0.5,
                }}
              >
                {result}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

function formatJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
